/**
 * CustomersPage — Listagem do módulo de Clientes (CRM).
 *
 * Shell redesenhado no padrão de listagem do Design System (espelha
 * `modules/users/pages/UsersPage`): toolbar de 3 zonas (breadcrumb + count |
 * busca centralizada | view-toggle + Filtros + primário), filterbar de chips
 * removíveis, bulk bar com seleção, tabela (desktop) / cards (grid + mobile) e
 * paginação. Busca e filtros continuam server-side (`useCustomers`); o modo de
 * visualização é persistido via `useCustomersPreferences` (localStorage).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  FiltersButton,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  Input,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  SortableHeadCell,
  ToolbarSearch,
  ViewToggle,
} from '@/shared/components/ui';
import { useModuleSearch } from '@/shared/components/search/ModuleSearchContext';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Icon } from '@/shared/icons';
import { usePermissions } from '@/hooks/usePermissions';

import { CustomerGridCard } from '../components/CustomerGridCard';
import { CustomerListItem } from '../components/CustomerListItem';
import { useCustomers, useCustomersPreferences, useDeleteCustomer, useUpdateCustomer } from '../hooks';
import type { CustomerResponse, CustomerServiceQuery } from '@troia-v3/types';

const PAGE_SIZE = 12;

type StatusFilter = 'all' | 'active' | 'inactive';
type TypeFilter = 'all' | 'PF' | 'PJ';
type SortKey = 'name' | 'type' | 'segment' | 'status';

/** Override CSS variables → tema claro fixo (popover de filtros, DS 1.10). */
const LIGHT_POPOVER_VARS: React.CSSProperties = {
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-muted-text': '#6b7280',
} as React.CSSProperties;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
];

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'PF e PJ' },
  { value: 'PF', label: 'Pessoa Física' },
  { value: 'PJ', label: 'Pessoa Jurídica' },
];

export const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { hasModulePermission } = usePermissions();
  const canCreate = hasModulePermission('customers', 'create');
  const canUpdate = hasModulePermission('customers', 'update');
  const canDelete = hasModulePermission('customers', 'delete');

  const { viewMode, setViewMode } = useCustomersPreferences();

  const [searchTerm, setSearchTerm] = useState('');
  useModuleSearch({ placeholder: 'Buscar clientes…', value: searchTerm, onChange: setSearchTerm });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [debouncedSegment, setDebouncedSegment] = useState('');
  const [page, setPage] = useState(1);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // "+" central do bottom-nav mobile → novo cliente (mockup `.m-fab`).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); navigate('/crm/customers/new'); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [navigate]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSegment(segmentFilter.trim()), 300);
    return () => clearTimeout(t);
  }, [segmentFilter]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, typeFilter, debouncedSegment]);

  const query: CustomerServiceQuery = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      filters: {
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
        ...(debouncedSegment ? { segment: debouncedSegment } : {}),
      },
    }),
    [page, debouncedSearch, statusFilter, typeFilter, debouncedSegment],
  );

  const { data, isLoading, error } = useCustomers(query);
  const deleteMutation = useDeleteCustomer();
  const updateMutation = useUpdateCustomer();

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (backend não expõe sort para clientes).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'type') cmp = a.type.localeCompare(b.type, 'pt-BR');
      else if (sort.key === 'segment') cmp = (a.segment ?? '').localeCompare(b.segment ?? '', 'pt-BR');
      else cmp = (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((c) => selectedIds.has(c.id));
  const someOnPageSelected = items.some((c) => selectedIds.has(c.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((c) => next.delete(c.id));
      else items.forEach((c) => next.add(c.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleOpen = (id: string) => navigate(`/crm/customers/${id}`);
  const handleEdit = (id: string) => navigate(`/crm/customers/${id}/edit`);

  const handleDelete = async (id: string, name: string) => {
    if (!(await confirm({ message: `Tem certeza que deseja excluir o cliente "${name}"?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteMutation.mutate(id);
  };

  const bulkDeactivate = () => {
    const targets = items.filter((c) => selectedIds.has(c.id) && c.status === 'active');
    targets.forEach((c) => updateMutation.mutate({ id: c.id, data: { status: 'inactive' } }));
    clearSelection();
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} cliente(s) selecionado(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) => deleteMutation.mutate(id));
    clearSelection();
  };

  // Rótulos e contagem de filtros ativos.
  const statusLabel = statusFilter !== 'all' ? STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? null : null;
  const typeLabel = typeFilter !== 'all' ? typeFilter : null;
  const segmentLabel = debouncedSegment || null;
  const activeFilterCount = [statusLabel, typeLabel, segmentLabel].filter(Boolean).length;

  const clearAllFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setSegmentFilter('');
    setFiltersOpen(false);
  };

  const filterChip = (text: string, onRemove: () => void, testId: string) => (
    <span
      className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
    >
      {text}
      <button
        onClick={onRemove}
        className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100"
        aria-label="Remover filtro"
        data-testid={testId}
      >
        <Icon name="x" className="w-3 h-3" />
      </button>
    </span>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) — mobile: busca desce pra 2ª linha full ── */}
      <PageToolbar
        left={
          <>
            {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate('/crm')}
              className="text-sm font-medium truncate hidden sm:inline"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              CRM
            </button>
            <Icon
              name="chevron-right"
              className="w-3.5 h-3.5 hidden sm:inline"
              style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }}
            />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>
              Clientes
            </span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
              data-testid="customers-count"
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada; mobile = linha própria full-width) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar cliente, empresa ou e-mail…"
            data-testid="customers-search"
          />
        }
        right={
          <>
            {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="customers-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-64 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 24px rgba(0,0,0,.12)',
                    ...LIGHT_POPOVER_VARS,
                  }}
                  data-testid="customers-filters-popover"
                >
                  {/* Status */}
                  <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>
                    Status
                  </p>
                  {STATUS_OPTIONS.map(({ value, label }) => {
                    const active = statusFilter === value;
                    return (
                      <button
                        key={value}
                        onClick={() => setStatusFilter(value)}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9';
                        }}
                        onMouseLeave={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        data-testid={`customers-filter-status-${value}`}
                      >
                        {label}
                        {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}

                  {/* Tipo */}
                  <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>
                    Tipo
                  </p>
                  {TYPE_OPTIONS.map(({ value, label }) => {
                    const active = typeFilter === value;
                    return (
                      <button
                        key={value}
                        onClick={() => setTypeFilter(value)}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9';
                        }}
                        onMouseLeave={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        data-testid={`customers-filter-type-${value}`}
                      >
                        {label}
                        {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}

                  {/* Segmento (texto livre) */}
                  <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>
                    Segmento
                  </p>
                  <div className="px-1 pb-1">
                    <Input
                      value={segmentFilter}
                      onChange={(e) => setSegmentFilter(e.target.value)}
                      placeholder="Filtrar por segmento…"
                      data-testid="customers-filter-segment"
                      aria-label="Filtrar por segmento"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="customers-viewmode"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Novo cliente (único primário) */}
          {canCreate && (
            <Button
              variant="primary"
              icon="user-plus"
              collapsibleLabel
              onClick={() => navigate('/crm/customers/new')}
              data-testid="customers-page-create"
            >
              Novo cliente
            </Button>
          )}
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div
          className="flex-none flex items-center flex-wrap gap-2 px-4 py-2"
          style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}
          data-testid="customers-filterbar"
        >
          {statusLabel && filterChip(`Status: ${statusLabel}`, () => setStatusFilter('all'), 'customers-chip-status')}
          {typeLabel && filterChip(`Tipo: ${typeLabel}`, () => setTypeFilter('all'), 'customers-chip-type')}
          {segmentLabel && filterChip(`Segmento: ${segmentLabel}`, () => setSegmentFilter(''), 'customers-chip-segment')}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="customers-clear-filters">
            Limpar tudo
          </button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div
          className="flex-none flex items-center gap-2.5 px-4 py-2"
          style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}
          data-testid="customers-bulk-bar"
        >
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>
            {selectedCount} selecionado(s)
          </span>
          <span className="flex-1" />
          {canUpdate && (
            <button
              onClick={bulkDeactivate}
              className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="customers-bulk-deactivate"
            >
              <Icon name="power" className="w-3.5 h-3.5" /> Desativar
            </button>
          )}
          {canDelete && (
            <button
              onClick={bulkDelete}
              className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold"
              style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }}
              data-testid="customers-bulk-delete"
            >
              <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
            </button>
          )}
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="customers-bulk-clear">
            Cancelar
          </button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}
              data-testid="customers-error"
            >
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar clientes: {(error as Error).message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando clientes..." />}

          {!isLoading && !error && items.length === 0 && (
            <div data-testid="customers-empty-state">
              <ListPageEmpty
                icon="briefcase"
                message="Nenhum cliente encontrado"
                description={
                  searchTerm || activeFilterCount > 0
                    ? 'Tente ajustar a busca ou os filtros.'
                    : 'Cadastre o primeiro cliente para vincular contatos, tickets e projetos'
                }
                {...(canCreate
                  ? { actionLabel: 'Novo cliente', onAction: () => navigate('/crm/customers/new') }
                  : {})}
              />
            </div>
          )}

          {/* Grid (Cards) — mockup `.cust-grid{grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:16px}` */}
          {/* F34: cards SEM checkbox — seleção em massa só na view de tabela */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
              {sortedItems.map((customer: CustomerResponse) => (
                <CustomerGridCard
                  key={customer.id}
                  customer={customer}
                  onOpen={handleOpen}
                  {...(canUpdate ? { onEdit: handleEdit } : {})}
                  {...(canDelete ? { onDelete: (id: string) => handleDelete(id, customer.name) } : {})}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((customer: CustomerResponse) => (
                  <CustomerGridCard
                    key={customer.id}
                    customer={customer}
                    onOpen={handleOpen}
                    {...(canUpdate ? { onEdit: handleEdit } : {})}
                    {...(canDelete ? { onDelete: (id: string) => handleDelete(id, customer.name) } : {})}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div
                className="hidden md:block rounded-xl overflow-hidden"
                style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
              >
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onToggle={toggleSelectAllOnPage}
                        data-testid="customers-select-all"
                      />
                      <SortableHeadCell<SortKey> label="Cliente" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Tipo" sortKey="type" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Segmento" sortKey="segment" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Contato" />
                      <SortableHeadCell<SortKey> label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((customer: CustomerResponse) => (
                      <CustomerListItem
                        key={customer.id}
                        customer={customer}
                        selected={selectedIds.has(customer.id)}
                        onToggleSelect={toggleSelect}
                        onOpen={handleOpen}
                        {...(canUpdate ? { onEdit: handleEdit } : {})}
                        {...(canDelete ? { onDelete: (id: string) => handleDelete(id, customer.name) } : {})}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação F48: totalItems/pageSize/itemLabel — "Mostrando X–Y de N clientes" */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (data.total ?? 0) > 0 && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={setPage}
              totalItems={data.total}
              pageSize={PAGE_SIZE}
              itemLabel="clientes"
            />
          )}
        </div>
      </div>
    </div>
  );
};

CustomersPage.displayName = 'CustomersPage';
